import React, { useState } from 'react'
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  IconButton,
  Tooltip,
  useTheme as useMuiTheme,
} from '@mui/material'
import { Add, Delete } from '@mui/icons-material'
import { useAuth } from '../../context/AuthContext'
import { useTheme } from '../../context/ThemeContext'

const ResumeSelector = ({ onResumeEditorSelect }) => {
  const { currentUser } = useAuth()
  const { isDarkMode } = useTheme()
  const theme = useMuiTheme()
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [selectedResumeId, setSelectedResumeId] = useState('')
  const [deleting, setDeleting] = useState(false)
  const [loading, setLoading] = useState(false)

  // This would be replaced with actual resume data from a context or API
  const resumes = [
    { id: 'software_engineer_resume', name: 'Software Engineer Resume' },
    { id: 'data_scientist_resume', name: 'Data Scientist Resume' },
  ]

  const handleResumeChange = (event) => {
    const resumeId = event.target.value
    setSelectedResumeId(resumeId)
    // Would load the resume data here
  }

  const handleCreateNew = () => {
    // Would create a new resume here
    setSelectedResumeId('')
  }

  const handleDeleteClick = () => {
    if (selectedResumeId) {
      setDeleteDialogOpen(true)
    }
  }

  const handleDeleteConfirm = async () => {
    setDeleting(true)
    try {
      // Would delete the resume here
      setSelectedResumeId('')
    } finally {
      setDeleting(false)
      setDeleteDialogOpen(false)
    }
  }

  if (!currentUser) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant='body2' color='text.secondary'>
          Please log in to manage resumes
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography
        variant='h6'
        gutterBottom
        sx={{ cursor: 'pointer', color: theme.palette.mode === 'dark' ? 'secondary.dark' : 'secondary.light' }}
        onClick={() => onResumeEditorSelect && onResumeEditorSelect()}
      >
        Resume Manager
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id='resume-select-label'>Select Resume</InputLabel>
            <Select
              labelId='resume-select-label'
              id='resume-select'
              value={selectedResumeId}
              label='Select Resume'
              onChange={handleResumeChange}
              disabled={loading}
            >
              <MenuItem value=''>
                <em>None</em>
              </MenuItem>
              {resumes.map((resume) => (
                <MenuItem key={resume.id} value={resume.id}>
                  {resume.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Button
              startIcon={<Add />}
              onClick={handleCreateNew}
              variant='outlined'
              size='small'
              sx={{
                color: theme.palette.mode === 'dark' ? 'secondary.dark' : 'secondary.light',
              }}
            >
              New Resume
            </Button>

            <Tooltip title={selectedResumeId ? 'Delete resume' : 'Select a resume to delete'}>
              <span>
                <IconButton
                  disabled={!selectedResumeId}
                  onClick={handleDeleteClick}
                  size='small'
                  sx={{ color: theme.palette.mode === 'dark' ? 'secondary.dark' : 'secondary.light' }}
                >
                  <Delete />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </>
      )}

      <Dialog open={deleteDialogOpen} onClose={() => !deleting && setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Resume</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to delete this resume? This action cannot be undone.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color='error' disabled={deleting} startIcon={deleting ? <CircularProgress size={20} /> : null}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default ResumeSelector
